import React from 'react'
import {Header, List, Message, Table} from 'semantic-ui-react'
import SyntaxHighlighter from 'react-syntax-highlighter'
import {docco} from 'react-syntax-highlighter/dist/esm/styles/hljs'
import {stripIndent} from 'common-tags'


const ScriptMockResponseActivation = () => {
  return (
    <div className='doc-section'>
      <Header as='h2'>Script Mock Response Activation</Header>
      <p>
        By default Service Mocks allows you to explicitly activate one response at a time. However, there are scenarios
        where it's necessary to determine what response is active based on the request data you are receiving.
        In order to allow for this Service Mocks will let you specify a response as being "scripted" which means it
        has access to a Javascript API that will allow you interpret the incoming request and decide if or when the
        response should be considered active.
      </p>
      <Header as='h3' dividing>Steps:</Header>
      <List relaxed divided>
        <List.Item>1. Navigate to service/version/environment/resource you wish to work with</List.Item>
        <List.Item>2. Click on the "Cog" icon and then the "Edit" icon for the response you want to script</List.Item>
        <List.Item>3. Click on "Settings" tab and toggle the "Scripted" option on.</List.Item>
        <List.Item>4. Click on the "Script" tab that is now displayed.</List.Item>
        <List.Item>5. Add script to text area using API/Namespace described below.</List.Item>
        <List.Item>6. Click the "Update" button</List.Item>
      </List>
      <Header as='h3' dividing>Javascript API</Header>
      <Header as='h4'>Request</Header>
      <Message>
        <Message.Header as='h5'>Notes:</Message.Header>
        <Message.List>
          <Message.Item>
            {'Path is the resource path: /m/:accountName/:serviceName/:versionName/:environmentName/<HERES_THE_PATH>'}
          </Message.Item>
          <Message.Item>The body will default to plain text</Message.Item>
          <Message.Item>
            If a Content-Type contains "xml" in the text, the mock will automatically parse the text body to
            a JSON object using the <a href='https://github.com/Leonidas-from-XIV/node-xml2js'
                                       target='_blank'>xml2js</a> library however it will place this parsed value on a
            key named
            "xmlBodyAsJson" and it will leave the "body" attribute as plain text.
          </Message.Item>
          <Message.Item>
            The entire "req" key/value will be available to you for use in your header or body templates if you have
            created them
          </Message.Item>
        </Message.List>
      </Message>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Property</Table.HeaderCell>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell>Default</Table.HeaderCell>
            <Table.HeaderCell>Example</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>req.verb</Table.Cell>
            <Table.Cell>HTTP Verb used in request</Table.Cell>
            <Table.Cell>n/a</Table.Cell>
            <Table.Cell>POST</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>req.path</Table.Cell>
            <Table.Cell>Mock path used in request</Table.Cell>
            <Table.Cell>/</Table.Cell>
            <Table.Cell>/some-mock-resource</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>req.headers</Table.Cell>
            <Table.Cell>Object containing request header key/value pairs</Table.Cell>
            <Table.Cell>{JSON.stringify({})}</Table.Cell>
            <Table.Cell>{JSON.stringify({"Accept": "application/json"})}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>req.query</Table.Cell>
            <Table.Cell>Object containing query string key/value pairs</Table.Cell>
            <Table.Cell>{JSON.stringify({})}</Table.Cell>
            <Table.Cell>?name=john equals {JSON.stringify({"name": "john"})}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>req.params</Table.Cell>
            <Table.Cell>Object containing path param key/value pairs</Table.Cell>
            <Table.Cell>{JSON.stringify({})}</Table.Cell>
            <Table.Cell>{JSON.stringify({"id": "<val>"})}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>req.body</Table.Cell>
            <Table.Cell>Request body. Object if json</Table.Cell>
            <Table.Cell>string</Table.Cell>
            <Table.Cell>{`{"foo":"bar"}`}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>req.xmlBodyAsJson</Table.Cell>
            <Table.Cell>XML request body as JSON (see req. notes)</Table.Cell>
            <Table.Cell>string</Table.Cell>
            <Table.Cell>{`{"foo":"bar"}`}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <Header as='h4'>Response</Header>
      <Message>
        <Message.Header has='h5'>Notes:</Message.Header>
        <Message.List>
          <Message.Item>The values for status, headers, body, and sleep attributes are defaulted to what was configured
            via the Console or API for the response being scripted</Message.Item>
        </Message.List>
      </Message>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Property</Table.HeaderCell>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell>Default</Table.HeaderCell>
            <Table.HeaderCell>Example</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>res.status</Table.Cell>
            <Table.Cell>Valid HTTP Status Code</Table.Cell>
            <Table.Cell>200</Table.Cell>
            <Table.Cell>{'<400|500>'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>res.headers</Table.Cell>
            <Table.Cell>Object containing response header key/value pairs</Table.Cell>
            <Table.Cell>{'{}'}</Table.Cell>
            <Table.Cell>{'{"Content-Type": "application/json"}'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>res.body</Table.Cell>
            <Table.Cell>Response body</Table.Cell>
            <Table.Cell>any</Table.Cell>
            <Table.Cell>"Hello World" --or- {`{"foo":"bar"}`}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>res.active</Table.Cell>
            <Table.Cell>Boolean to set response active</Table.Cell>
            <Table.Cell>false</Table.Cell>
            <Table.Cell>true</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>res.sleep</Table.Cell>
            <Table.Cell>Milliseconds to delay response</Table.Cell>
            <Table.Cell>-1</Table.Cell>
            <Table.Cell>1000</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>res.vars</Table.Cell>
            <Table.Cell>Object containing key/value pairs for templating</Table.Cell>
            <Table.Cell>{'{}'}</Table.Cell>
            <Table.Cell>{'{"var_computed": "100"}'}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <Header as='h4'>Utilities</Header>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Property</Table.HeaderCell>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell>Default</Table.HeaderCell>
            <Table.HeaderCell>Example</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>_</Table.Cell>
            <Table.Cell>Lodash</Table.Cell>
            <Table.Cell><a href='https://lodash.com/docs/4.17.15' target='_blank'>docs</a></Table.Cell>
            <Table.Cell>{'_.get(req.body, "first")'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>JSON</Table.Cell>
            <Table.Cell>{'The full JSON API including parse(<object>)/stringify(<string>)'}</Table.Cell>
            <Table.Cell>{'{parse: fn, stringify: fn}'}</Table.Cell>
            <Table.Cell>{'JSON.stringify(req.body)'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>XML</Table.Cell>
            <Table.Cell>A wrapper around xml2js to parse string of xml to object rep. XML.</Table.Cell>
            <Table.Cell>{'{parseAsJson: fn}'}</Table.Cell>
            <Table.Cell>{'XML.parseAsJson(req.body)'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>console</Table.Cell>
            <Table.Cell>A light impl. of the typical console API, only inclusive of log func.</Table.Cell>
            <Table.Cell>{'{log: fn}'}</Table.Cell>
            <Table.Cell>{'console.log("statement")'}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <Header as='h3' dividing>Javascript Namespace</Header>
      <div>
        <SyntaxHighlighter language="javascript" style={docco}>
          {
            stripIndent`
              {
                // request data
                req: {
                  verb: "<verb_used_to_issue_request_to_mock>"
                  path: "<path_provided_to_mock>",
                  headers: {
                    "<header_key>": "<header_value>"
                  },    
                  query: {
                    "<query_param_key>": "<query_param_value">
                  },   
                  params: {
                    "<query_param_key>": "<query_param_value">
                  },
                  body: <text|json|xml>,
                  xmlBodyAsJson: {}
                 }
                },
                // response data
                res: {        
                  status: <http_status_code>
                  active: <boolean_to_activate_response>,
                  headers: {
                    "<header_key>": "<header_value>"
                  },    
                  body: <text|json|xml>,
                  sleep: <integer_delay_in_ms_to_apply>,
                  vars: {
                    "var_one_key": "some_string_value",
                    "var_two_key": true,
                    "var_three_key": 123 
                  }
                },
                // utilities
                _: <lodash>,
                JSON: { parse(<string>), stringify(<string>) },
                XML: { parseAsJson(<string>) } 
              }
            `
          }
        </SyntaxHighlighter>
      </div>
      <Header as='h3' dividing>Example Script</Header>
      <div>
        <SyntaxHighlighter language="javascript" style={docco}>
          {
            stripIndent
              `
              // Logic is checking this resource?
              // POST /customers/{id}?name=<sometext>
              // HEADERS: 'Accept: application/json'
              // BODY: { "name": "john", "address": "123 test drive" }
  
              console.log('entering script logic')
  
              // let's initialize a variable for use later in our logic. see below
              let setResponseActiveOnStep = 'unknown' 
             
              // starting with assumption that response is inactive
              res.active = false
              console.log("set response inactive to start")
  
              // if name query param value contains certain text then activate response
              if(!_.isNil(req.query.text) && req.query.text.indexOf('john') > -1) {
                res.active = true
                setResponseActiveOnStep = 'QUERY_PARAM_CHECK'
                console.log("response is active because special text found in name query param")
              }
              
              //if id path param is in predefined list then proceed
              if(['1', '2'].includes(req.query.params.id)) {
                res.active = true
                setResponseActiveOnStep = 'PATH_PARAM_CHECK'
                console.log("response is active because special idfound in name path param")
              }
              
              //if "Accepts" header value equals "application/xml" let's set this inactive
              //because this response is configured to return JSON only.
              if(req.headers['Content-Type'] === 'application/xml') {
                res.active = false
                setResponseActiveOnStep = 'ACCEPT_HEADER_CHECK'
                console.log("response is inactive because client says they want XML instead.")
              }
              
              // let's pass along the name attribute from the request body to the name attribute of the 
              // response given this is dynamic. 
              res.body.name = req.body.name
              
              // this is a little contrived, but let's also set a variable on the response that we can
              // reference in our response template to determine how this logic behaved. We'll refer to 
              // this variable in the "How To" on templating. Go check that out. 
              res.vars.setResponseActiveOnStep = setResponseActiveOnStep
              
              // last but not least let's set the sleep value if a query param is provided saying to do so.
              if(req.query.sleep) {
                try {
                  res.sleep = parseInt(req.query.sleep)
                } catch(e) {
                 // let's log this for us in debugging.
                  console.log("ERROR PARSING SLEEP VALUE: ", req.query.sleep)
                }
              }
              
              // No need to return anything. Setting res.active does the trick and whatever it was last
              // set to will be what is used when the script concludes. Let's log it for final visibility.
              console.log("res.active = ", res.active)
            `
          }
        </SyntaxHighlighter>
      </div>
    </div>
  )
}

export {ScriptMockResponseActivation}

export default ScriptMockResponseActivation